import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import EuroIcon from '@mui/icons-material/Euro';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PodcastsOutlinedIcon from '@mui/icons-material/PodcastsOutlined';
import { Box, Typography } from '@mui/material';
import localforage from 'localforage';
import { useSnackbar } from 'notistack';
import { useGetFeatureFlags } from 'src/services/api/api';

import { BACKEND_URL } from 'src/constants';
import { useConfirm } from 'src/contexts/ConfirmContext';
import { useTopBar } from 'src/contexts/TopBarContext';
import { TestIds } from 'src/testIds';
import { useUserStore } from 'src/services/auth/auth';
import { routes } from 'src/services/routing';
import Button from 'src/components/Button/Button';
import Page from 'src/components/Page/Page';

import useStyles from './ProfileView.styles';

const ProfileView: FC = () => {
  const { profileItem } = useStyles();
  const history = useHistory();
  const { user } = useUserStore();
  const topBar = useTopBar();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { logout } = useUserStore();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(
    () => topBar.setTitle(`${user?.firstName} ${user?.lastName}`),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.firstName, user?.lastName],
  );

  const handleLogout = async () => {
    try {
      await confirm({
        description: t('Views.Profile.Logout.dialogDescription'),
        primaryText: t('Views.Profile.Logout.title'),
        title: t('Views.Profile.Logout.dialogTitle'),
      });
      await logout();
      await localforage.removeItem('userState');
      window.location.replace(routes.login.path);
    } catch (reason) {
      if (reason === 'onSecondary') {
        enqueueSnackbar(t('Views.Profile.Logout.logoutCanceled'), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(t('Views.Profile.Logout.logoutError'), {
          variant: 'error',
        });
      }
    }
  };
  const noLinkStyle = {};

  const flags = useGetFeatureFlags([
    'can-view-vacation-link',
    'can-view-forms-link',
    'can-view-support-link',
    'can-view-payroll-link',
    'can-view-documents-link',
  ]);
  const canViewVacationLink = flags['can-view-vacation-link'];
  const canViewFormsLink = flags['can-view-forms-link'];
  const canViewSupportLink = flags['can-view-support-link'];
  const canViewPayrollLink = flags['can-view-payroll-link'];
  const canViewDocumentsLink = flags['can-view-documents-link'];

  return (
    <Page
      data-test-id={TestIds.views.profile.pageWrapper}
      title={t('Views.Profile.title')}
    >
      <Box display="flex" justifyContent="left" mb={2} mt={4}>
        <Typography variant="h3" fontSize="18px">
          {t('Views.Profile.TimeManagement.title')}
        </Typography>
      </Box>

      <Box
        className={profileItem}
        onClick={() => history.push(routes.app.routes.blockTimes.path)}
      >
        <Box display="flex">
          <EmojiPeopleIcon fontSize="small" />
          <Typography ml={1}>
            {t('Views.Profile.TimeManagement.blockTimes')}
          </Typography>
        </Box>
        <ChevronRightOutlinedIcon fontSize="small" />
      </Box>

      {canViewVacationLink && (
        <a
          className={profileItem}
          style={{ color: 'black', textDecoration: 'none' }}
          href={`${BACKEND_URL}/absence/`}
        >
          <Box display="flex">
            <BeachAccessOutlinedIcon fontSize="small" />
            <Typography ml={1}>{t('Views.Profile.Links.vacation')}</Typography>
          </Box>
          <ChevronRightOutlinedIcon fontSize="small" />
        </a>
      )}

      {canViewFormsLink && (
        <a
          className={profileItem}
          style={{ color: 'black', textDecoration: 'none' }}
          href={`${BACKEND_URL}/forms/`}
        >
          <Box display="flex">
            <DescriptionOutlinedIcon fontSize="small" />
            <Typography ml={1}>{t('Views.Profile.Links.forms')}</Typography>
          </Box>
          <ChevronRightOutlinedIcon fontSize="small" />
        </a>
      )}

      <a
        className={profileItem}
        style={{ color: 'black', textDecoration: 'none' }}
        href={`${BACKEND_URL}/news/`}
      >
        <Box display="flex">
          <PodcastsOutlinedIcon fontSize="small" />
          <Typography ml={1}>
            {t('Views.Profile.Links.podcasts')}
          </Typography>
        </Box>
        <ChevronRightOutlinedIcon fontSize="small" />
      </a>

      {canViewSupportLink && (
        <a
          className={profileItem}
          style={{ color: 'black', textDecoration: 'none' }}
          href={`${BACKEND_URL}/support/`}
        >
          <Box display="flex">
            <HelpOutlineIcon fontSize="small" />
            <Typography ml={1}>{t('Views.Profile.Links.support')}</Typography>
          </Box>
          <ChevronRightOutlinedIcon fontSize="small" />
        </a>
      )}

      {canViewPayrollLink && (
        <a
          className={profileItem}
          style={{ color: 'black', textDecoration: 'none' }}
          href={`${BACKEND_URL}/payroll/`}
        >
          <Box display="flex">
            <EuroIcon fontSize="small" />
            <Typography ml={1}>{t('Views.Profile.Links.payroll')}</Typography>
          </Box>
          <ChevronRightOutlinedIcon fontSize="small" />
        </a>
      )}

      {canViewDocumentsLink && (
        <a
          className={profileItem}
          style={{ color: 'black', textDecoration: 'none' }}
          href={`${BACKEND_URL}/documents/`}
        >
          <Box display="flex">
            <FolderOutlinedIcon fontSize="small" />
            <Typography ml={1}>{t('Views.Profile.Links.documents')}</Typography>
          </Box>
          <ChevronRightOutlinedIcon fontSize="small" />
        </a>
      )}

      <Box display="flex" justifyContent="center" mt={8}>
        <Button
          color="secondary"
          loading={false}
          size="medium"
          type="submit"
          variant="contained"
          onClick={() => handleLogout()}
        >
          {t('Views.Profile.Logout.title')}
        </Button>
      </Box>
    </Page>
  );
};

export default ProfileView;
